import Alpine from 'alpinejs';
import { Meilisearch } from 'meilisearch';
import debounce from 'lodash/debounce';
import { off } from 'process';

Alpine.data('Search', (recommendedResults) => {
    let client = null;
    let index = null;
    let cardTypes = {
        Services: {
            name: 'Service',
            background: 'bg-quaternary-300'
        },
        Events: {
            name: 'Event',
            background: 'bg-tertiary-300'
        },
        default: {
            name: 'Information',
            background: 'bg-secondary-200'
        }
    }

    return {
        processing: true,
        searchIndex: null,
        searchInput: '',
        lastSearchInput: null,
        searchResults: [],
        perPage: 12,
        currentPage: 1,
        estimatedTotalHits: null,
        filterSelection: {
            ageGroupCategories: [],
            diagnosisCategories: [],
            searchGroups: []
        },
        filterObjects: {},
        filterObjectIsEmpty: true,
        recommendedResults: null,

        async init() {
            client = new Meilisearch({
                host: window.frameCreative.meilisearchHost,
                apiKey: window.frameCreative.meilisearchKey
            });

            let indexName = window.frameCreative.environment == 'production' ? 'Novita_Org' : 'Novita_Dev';
            
            index = client.index(indexName);
            this.recommendedResults = Array.from(recommendedResults).map((result) => this.formatResult(result));

            this.getParams();
            this.setupWatchers();

            this.requestResults(false);

        },

        offset() {
            return this.perPage * (this.currentPage - 1);
        },
        
        setupWatchers() {
            this.$watch('filterSelection', () => this.filterSelectionChanged());
        },

        // This is called when any filter selection changes
        filterSelectionChanged() {
            this.currentPage = 1;
            // When the filter selection changes, we build out a list of filter objects to ensure we get all the labels and values
            // Primarily used for showing which filter options are selected via 'pills'
            Object.entries(this.filterSelection).forEach(([key, value]) => {
                
                if (Array.isArray(value)) {
                    this.filterObjects[key] = [];

                    value.forEach((val, index) => {
                        const element = document.querySelector(`*[name="${key}[]"][value="${val}"]`);
                        
                        this.filterObjects[key][index] = { label: element.labels?.[0]?.textContent?.trim(), value: val };
                    });
                } else {
                    const element = document.querySelector(`*[name="${key}"]`);
                    const label = element.selectedOptions ? (element.selectedOptions?.[0]?.value ? element.selectedOptions?.[0]?.text : null) : element.labels?.[0]?.textContent?.trim();
                    this.filterObjects[key] = { label, value: value };
                }
            });
            
            // Get a new list of results
            this.requestResults();
        },

        // Builds out the query for the filter are per Meilisearch
        // https://www.meilisearch.com/docs/reference/api/search#filter
        buildFilterQuery() {
            return Object.entries(this.filterSelection).reduce((acc, [key, selected]) => {
                if (selected !== '' && (!Array.isArray(selected) || selected.length > 0)) {
                    let keyName = key;

                    if(key != 'searchGroups'){
                        keyName = `${key}.id`
                    } 
                    
                    if (Array.isArray(selected)) {
                        acc.push(`${keyName} IN [${selected.join(',')}]`);
                    } else {
                        acc.push(`${keyName} = '${selected}'`);
                    }
                }

                return acc;
            }, []).join(' AND ');
        },

        // Performs a text search on enter or click of the search button
        // This just allows us to check if the search input has changed
        textSearch() {
            if (this.searchInput === this.lastSearchInput) {
                return;
            }

            this.currentPage = 1;
            this.requestResults();
        },

        // Removed a filter based on the type and value
        // Used for the filter pills
        removeFilter(type, value) {
            if (Array.isArray(this.filterSelection[type])) {
                this.filterSelection[type] = this.filterSelection[type].filter(item => item !== value);

                if (this.filterSelection[type].length === 0) {
                    this.filterSelection[type] = [];
                }
            } else if (this.filterSelection[type] && this.filterSelection[type] === value) {
                this.filterSelection[type] = '';
            }
        },
        //Remove all filters
        removeFilters(){

            Object.entries(this.filterSelection).forEach(([key]) => {
                if (Array.isArray(this.filterSelection[key])) {
                    this.filterSelection[key] = [];
                } else {
                    this.filterSelection[key] = '';
                }
            });

        },

        // Main method to request results
        // This allows us to debounce the request if needed
        async requestResults(useDebounce = true) {
            this.processing = true;

            if (!this._debouncedFetchResults) {
                this._debouncedFetchResults = debounce(this._fetchResults.bind(this), 750);
            }

            if (useDebounce) {
                await this._debouncedFetchResults();
            } else {
                await this._fetchResults();
            }
        },

        // Internal method to fetch results
        async _fetchResults() {

            this.setParams();

            try {
                const data = await index.search(this.searchInput, {
                    filter: this.buildFilterQuery(),
                    limit: this.perPage,
                    offset: this.offset()
                });

                this.lastSearchInput = this.searchInput;
                this.estimatedTotalHits = data.estimatedTotalHits;

                const results = data.hits.map(result => this.formatResult(result));

                if(this.currentPage > 1){
                    this.searchResults = [...this.searchResults, ...results];
                } else {
                    this.searchResults = [...results];
                }
            } catch (e) {
                console.error('Search request failed', e);
                this.lastSearchInput = null; // allow the same search to be retried
            } finally {
                this.processing = false;
            }
        },

        formatResult(result){
            return {
                ...result,
                searchType: cardTypes[result.sectionName] ? cardTypes[result.sectionName].name : cardTypes.default.name,
                searchColor: cardTypes[result.sectionName] ? cardTypes[result.sectionName].background : cardTypes.default.background,
            };
        },

        loadMore(){
            this.currentPage++;

            this.requestResults();
        },

        getParams(){
            const urlParams = new URLSearchParams(window.location.search);
            const searchQuery = urlParams.get('s');

            this.searchInput = searchQuery ? searchQuery : '';
            
            Object.keys(this.filterSelection)
                .forEach((key) => {
                    let values = urlParams.getAll(key + '[]');

                    this.filterSelection[key] = values;
                });
        },

        setParams(){
            let newQuery = this.getURLQueryString();

            const newLocation = location.origin + location.pathname + "?" + newQuery;

            history.pushState("", "", newLocation);

        },

        getURLQueryString(){
            let segments = [];

            if(this.searchInput){
                segments.push(`s=${this.searchInput}`)
            }

            Object.keys(this.filterObjects)
                .forEach((key) => {
                    if(this.filterObjects[key].length > 0){
                        let values = this.filterObjects[key];
                        let segment = null
                        
                        if(values.length > 1){
                            segment = values.map((v, index) => {
                                return encodeURIComponent(key) + '[]=' + v.value;
                            })
                            .join("&");
                        } else {
                            segment = encodeURIComponent(key) + "[]=" + values[0].value
                        }
                        segments.push(segment);
                    }
                });
            
            return segments.join('&');
        }
    }

});